<!--
    Component is used from plugin. Check 'PasswordConfirm.plugin.js' for details.
-->

<template>
  <b-modal
    ref="passwordConfirmModal"
    modal-class="confirm-modal"
    :size="size"
    :title="title"
    centered
    @hidden="reset"
  >
    <template slot="modal-header">
      <h2>{{ title }}</h2>
      <p>{{ subTitle }}</p>
    </template>
    <div>
      <p>{{ message }}</p>
      <div>
        <ValidationObserver
          ref="customFieldObserver"
          v-slot="{ valid, dirty, pristine }" >

          <b-form-group>
            <label
              for="passwordConfirmation"
              class="flex flex-align-center">Password</label>
            <ValidationProvider
              v-slot="{ errors }"
              rules="required"
              mode="passive"
              name="Password" >
              <b-input
                id="passwordConfirmation"
                v-model="passwordConfirmation"
                class="sb2-bordered sb2-hoverable sb2-focusable"
                :disabled="checkingPassword"
                name="passwordConfirmation"
                type="password"
                placeholder="Enter your password"
                autofocus
                @keyup.enter="confirm" />
              <div
                v-if="errors[0]"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </b-form-group>
        </ValidationObserver>
      </div>
      <div
        v-if="wrongPassword"
        class="flex flex-align-center"
        style="margin-top: 5px;" >
        <SBIcon
          class="mr-2"
          :icon="SBIconList.ERROR"
          :color="SBColors.redPrimary"
          :size="12" />
        <p
          v-if="wrongPassword"
          class="error account-settings__error m-0 p-0 red-text">Wrong password. Please try again.</p>
      </div>
    </div>
    <template slot="modal-footer">
      <div class="modal-footer__message">
        <div
          v-if="showUndoWarning"
          class="modal-footer__undo-warning flex flex-align-center">
          <span class="sb2-icon icon-error size-12"/>This action cannot be undone.
        </div>
      </div>
      <div class="modal-footer__buttons flex flex-row flex-nowrap">
        <b-button
          :disabled="checkingPassword"
          @click="hide" >{{ cancelButtonText }}</b-button>
        <b-button
          v-if="hasCallback"
          :disabled="checkingPassword"
          :variant="buttonVariant"
          :class="{loading: working}"
          @click="confirm" >
          <span>{{ confirmButtonText }}</span>
          <Spinner
            v-if="working"
            :size="14"
            :thickness="2"
            :color="SBColors.white" />
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import Spinner from '@/components/common/Spinner';

export default {
  name: 'PasswordConfirmModalComponent',
  components: {
    SBIcon,
    Spinner,
  },
  data() {
    return {
      passwordConfirmation: '',
      validationErrors: {},
      defaultTitle: 'Confirm action',
      defaultSize: 'sm',
      hasCallback: false,
      confirmButtonText: 'Continue',
      title: 'Confirm action',
      subTitle: 'To confirm this action, please enter your password.',
      message: '',
      variant: '',
      size: '',
      buttonVariant: 'primary',
      cancelButtonText: 'Cancel',
      wrongPassword: false,
      working: false,
      showUndoWarning: false,
      onConfirmCallback() {},
      SBIconList,
      SBColors,
    };
  },
  computed: {
    ...mapGetters({
      checkingPassword: 'user/checkingPassword',
    }),
  },
  beforeMount() {
    this.$infoModalEventBus.$on('showPasswordConfirmModal', (params) => {
      this.show(params);
    });
  },
  methods: {
    ...mapActions({
      requestConfirmationCode: 'user/requestConfirmationCode', // TODO
      checkPassword: 'user/checkPassword',
    }),
    show(params) {
      this.working = false;
      this.title = params.title || this.defaultTitle;
      this.message = params.message;
      this.size = params.size || this.defaultSize;
      if (typeof params.confirmCallback === 'function') {
        this.hasCallback = true;
        this.onConfirmCallback = params.confirmCallback;
      }
      if (params.type === 'ERROR') {
        this.variant = 'danger';
      } else if (params.type === 'WARNING') {
        this.variant = 'warning';
      } else {
        this.variant = 'info';
        if (!this.title) {
          this.title = 'INFO';
        }
      }
      this.confirmButtonText = params.confirmButtonText || 'Confirm';
      this.$refs.passwordConfirmModal.show();
    },
    hide() {
      this.$refs.passwordConfirmModal.hide();
    },
    confirm() {
      this.wrongPassword = false;
      this.$refs.customFieldObserver.validate()
        .then((valid) => {
          if (valid) {
            this.working = true;
            this.wrongPassword = false;
            this.checkPassword({
              password: this.passwordConfirmation,
            }).then((passwordValid) => {
              if (passwordValid) {
                this.onConfirmCallback();
                this.hide();
              } else {
                this.working = false;
                this.wrongPassword = true;
              }
            });
          }
        });
    },
    reset() {
      this.passwordConfirmation = '';
      this.wrongPassword = false;
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .confirm-modal {
  .modal-content {
    .modal-header {
      padding: 20px;
      box-shadow: none;
      border-bottom: solid 1px $greyBorderModal;
      display: flex;
      flex-direction: column;

      h2 {
        font-size: 16px;
        font-weight: 500 !important;
        color: $blackPrimary;
        margin-bottom: 4px;
      }

      p {
        font-size: 14px;
        color: $blackUltralight;
      }
    }
    .modal-body {
      padding: 20px;
      font-size: 14px;
      font-weight: normal;
      line-height: 1.2;
      color: $blackPrimary;
      background-color: $greyUltralight;
    }
    .modal-footer {
      border-top: solid 1px $greyBorderModal;
      align-items: center;
      justify-content: space-between;
      flex-direction: row;
    }
    .modal-footer>* {
      margin: 0;
    }
    .modal-footer__message {
      // style if needed
    }
    .modal-footer__buttons {
      // style if needed
      .btn.loading {
        position: relative;

        span {
          visibility: hidden;
        }

        .loader {
          position: absolute;
          height: 100%;
          display: flex;
          align-items: center;
        }
      }
    }
    .modal-footer__undo-warning {
      color: $redPrimary;
      font-size: 14px;
      font-weight: 400;
      // icon 4px from text
      .sb2-icon {
        margin-right: 4px;
      }
    }
  }
}
</style>
